import React from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend, ResponsiveContainer, Label,
} from "recharts";
import ThinScroll from "../../shared/ThinScroll";
import ThemedChartTooltip from "../../shared/ThemedChartTooltip";
import AnalysisPanel, { PanelEmpty } from "../AnalysisPanel";
import { getCiReportPriceDistribution } from "../../../api/competitiveIntelligence";
import { PanelTakeaway, fmtRate, SERIES_HUES } from "../shared";
import { axisProps, axisLabelStyle } from "../shared/chartTheme";

// Spec comparison palette: you=blue, market=emerald. Hues from shared SERIES_HUES.
const COLOR_YOU = SERIES_HUES.blue;
const COLOR_MARKET = SERIES_HUES.emerald;

const PriceDistributionBody = ({ rows }) => {
  if (!rows.length) {
    return <PanelEmpty message="No price distribution data for the selected category." />;
  }

  const chartData = rows.map((row) => ({
    band: row.price_band,
    your: row.your_count ?? 0,
    market: row.market_count ?? 0,
  }));

  return (
    <div className="p-5 space-y-5">
      <div style={{ width: "100%", height: 260 }}>
        <ResponsiveContainer>
          <BarChart
            data={chartData}
            margin={{ top: 10, right: 10, left: 10, bottom: 10 }}
            barGap={2}
            barCategoryGap="25%"
          >
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(156,163,175,0.2)" />
            <XAxis
              dataKey="band"
              {...axisProps}
            />
            <YAxis
              {...axisProps}
              allowDecimals={false}
            >
              <Label
                value="Sites (count)"
                angle={-90}
                position="insideLeft"
                offset={10}
                style={axisLabelStyle}
              />
            </YAxis>
            <ThemedChartTooltip itemSorter={(item) => (item.dataKey === "your" ? 0 : 1)} />
            <Legend
              wrapperStyle={{ fontSize: 12 }}
              itemSorter={(item) => (item.dataKey === "your" ? 0 : 1)}
            />
            <Bar dataKey="your" name="Your Sites" fill={COLOR_YOU} radius={[2, 2, 0, 0]} />
            <Bar dataKey="market" name="Market Sites" fill={COLOR_MARKET} radius={[2, 2, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <ThinScroll className="overflow-x-auto responsive-card-table-wrapper">
        <table className="w-full text-sm responsive-card-table">
          <thead className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
            <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
              <th className="px-3 py-2.5 text-left font-semibold">Price Band</th>
              <th className="px-3 py-2.5 text-right font-semibold">Your Sites</th>
              <th className="px-3 py-2.5 text-right font-semibold">Market Sites</th>
              <th className="px-3 py-2.5 text-right font-semibold">Your Available</th>
              <th className="px-3 py-2.5 text-right font-semibold">Your Avg in Band</th>
              <th className="px-3 py-2.5 text-right font-semibold">Market Avg in Band</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.price_band} className="border-b dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition">
                <td data-label="Price Band" className="px-3 py-2.5 font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">{row.price_band}</td>
                <td data-label="Your Sites" className="px-3 py-2.5 text-right font-semibold whitespace-nowrap" style={{ color: COLOR_YOU }}>{row.your_count ?? 0}</td>
                <td data-label="Market Sites" className="px-3 py-2.5 text-right font-semibold whitespace-nowrap" style={{ color: COLOR_MARKET }}>{row.market_count ?? 0}</td>
                <td data-label="Your Available" className="px-3 py-2.5 text-right text-gray-500 dark:text-gray-400 whitespace-nowrap">{row.your_available_count ?? 0}</td>
                <td data-label="Your Avg in Band" className="px-3 py-2.5 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">{fmtRate(row.your_avg_in_band)}</td>
                <td data-label="Market Avg in Band" className="px-3 py-2.5 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">{fmtRate(row.market_avg_in_band)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </ThinScroll>
    </div>
  );
};

const PriceDistributionPanel = ({ clientId, categoryCode, say }) => (
  <AnalysisPanel
    id={`price-distribution-${clientId}-${categoryCode || "all"}`}
    title="Price Distribution"
    tip="Histogram of how many of your sites and how many market sites sit in each price band ($0–49, $50–74, etc.). Shows where pricing clusters and whether you're at the same band as the bulk of the market."
    description="Where your inventory clusters vs the market across price bands."
    badge={categoryCode ? categoryCode.toUpperCase() : null}
    fetcher={() => getCiReportPriceDistribution(clientId, { categoryCode: categoryCode || "rv" })}
    deps={[clientId, categoryCode]}
  >
    {({ data }) => {
      const rows = Array.isArray(data) ? [...data].sort((rowA, rowB) => (rowA.band_min ?? 0) - (rowB.band_min ?? 0)) : [];
      return (
        <>
          <PanelTakeaway panelKey="price_distribution" say={say} />
          <PriceDistributionBody rows={rows} />
        </>
      );
    }}
  </AnalysisPanel>
);

export default PriceDistributionPanel;
